import { Language } from '../i18n'

/** Usage: `arrays.reduce(reduceToSumOfItems, [])` */
export function reduceToSumOfItems<T extends number | null>(
  total: T[],
  current: T[],
): T[] {
  if (total.length === 0) {
    return current
  }
  if (current.length === 0) {
    return total
  }
  if (total.length !== current.length) {
    throw new Error('Invalid input when summing array items')
  }
  return current.map((value, i) => {
    const totalValue = total[i]
    if (value === null) {
      return totalValue
    }

    if (totalValue === null) {
      return value
    }

    return (value + totalValue) as T
  })
}

/** Usage: `sumItems(arrays)` */
export const sumItems = (itemArrays: number[][]): number[] =>
  itemArrays.reduce(reduceToSumOfItems, [])

export const subtractItems = (
  original: number[],
  remove: number[],
): number[] => {
  if (original.length === 0 || remove.length === 0) {
    return original
  }
  if (original.length !== remove.length) {
    throw new Error('Invalid input when subtracting array items')
  }
  return original.map((value, i) => value - remove[i])
}

/** Usage: `numbers.reduce(reduceToSum, 0)` */
export const reduceToSum = (total: number, current: number): number =>
  total + current

/** Usage: `sum(values)` */
export const sum = (values: number[]): number => values.reduce(reduceToSum, 0)

/** Usage: `average(values)`. Returns 0 if no values are given. */
export const average = (values: number[]): number =>
  values.length > 0 ? sum(values) / values.length : 0

export function groupBy<T>(
  items: T[],
  predicate: (item: T) => string,
): Record<string, T[]> {
  return items.reduce((result, item) => {
    const type = predicate(item)

    return {
      ...result,
      [type]: [...(result[type] ?? []), item],
    }
  }, {} as Record<string, T[]>)
}

export function groupIndicesByValue(items: number[]): Record<string, number[]> {
  return items.reduce((result, value, index) => {
    const type = value

    return {
      ...result,
      [type]: [...(result[type] ?? []), index],
    }
  }, {} as Record<string, number[]>)
}

/** Usage: `arrays.reduce(createReduceToSumOfGroupItems, [])` */
export const createReduceToSumOfGroupItems =
  (groupStartIndices: number[]) =>
  (groupedValues: number[], value: number, index: number): number[] => {
    if (groupStartIndices.includes(index)) {
      groupedValues.push(value)
    } else {
      // eslint-disable-next-line no-param-reassign
      groupedValues[groupedValues.length - 1] += value
    }
    return groupedValues
  }

export function createSortByProperty<T, K extends keyof T>(
  property: K,
  locale = Language.Finnish,
) {
  return (a: T, b: T): number =>
    String(a[property]).localeCompare(String(b[property]), locale)
}

export function sortByProperty<T, K extends keyof T>(
  objects: T[],
  property: K,
  locale = Language.Finnish,
): T[] {
  return [...objects].sort(createSortByProperty(property, locale))
}

export function isNotNull<T>(t: T | null): t is T {
  return t !== null
}

export function splitEvenly<T>(items: T[], maxSize: number): T[][] {
  const parts = Math.ceil(items.length / maxSize) || 1
  const splitIndex = Math.ceil(items.length / parts)

  return [...(Array(parts) as unknown[])].map((_, index) =>
    items.slice(index * splitIndex, (index + 1) * splitIndex),
  )
}
